import { gql } from '@apollo/client';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
  Avatar,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  tableCellClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { css, styled as muiStyled } from '@mui/material/styles';
import React from 'react';
import { useParams } from 'react-router-dom';

import AssessmentCard from './assessment-card';
import BulkAssignDrawer from './bulk-assign-drawer';
import { useDashboardDataContext } from './dashboard-context';
import { navigateToAssessment } from './helper';
import MonitoringTab from './monitoring/monitoring-tab';
import AssessmentAssignmentManager from '../../assessment/bundle-form/assessment-assignment-manager';
import { stringAvatar } from '../../assessment/helper';
import {
  getAssessmentGovernanceTab,
  type AssessmentGovernanceTab,
} from '../../assessment/utils/assessment-utils';
import { UserPlus } from '../../base/icon/user-plus';
import ProgressWithText from '../../progress-with-text';
import ModalComponent from '../../project-table/archive-project-modal';

import useSnackbarContext from '~/client/components/base/snackbar/snackbar-context';
import { spc } from '~/client/components/base/spacer';
import StatusBadge from '~/client/components/base/status-badge';
import { useIsBetaUser } from '~/client/components/beta-only';
import PolicyItem from '~/client/components/policy/policy-item';
import { usePermissions } from '~/client/contexts/permissions-context';
import { exportToCsv } from '~/client/helpers/csv-export';
import { useDebounce } from '~/client/hooks/policy';
import { useSelector } from '~/client/hooks/redux';
import { useUserProfile } from '~/client/hooks/user-profile';
import {
  aiDashboardUtils,
  aiSystemAssessmentRouteUtil,
  candidateAgentAssessmentRouteUtil,
  candidateAgentsDetailsRouteUtil,
  candidateModelAssessmentRouteUtil,
  candidateModelsDetailsRouteUtil,
  functionalModelAssessmentRouteUtil,
  functionalModelsRouteUtil,
} from '~/client/routes';
import * as GQL from '~/graphql/generated/client';
import AssessmentStatusText from '~c/components/assessment/assessment-status';
import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import Card from '~c/components/base/card';
import { ArrowRight } from '~c/components/base/icon';
import SearchAndFiltersBar from '~c/components/base/search-and-filters-bar';
import { TableAction } from '~c/components/base/table';
import Text from '~c/components/base/text';

// Styled table components (theme-aware for dark mode)
const StyledTableCell = muiStyled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: '14px',
    fontWeight: 600,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    fontStyle: 'normal',
    fontWeight: 'normal',
  },
}));

const StyledTableRow = muiStyled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  cursor: 'pointer',
}));

interface Props extends React.PropsWithChildren {
  className?: string;
  assessments: GQL.AssessmentFragment[];
  project_: GQL.Project;
  refetch: ((variables?: any) => Promise<any>) | undefined;
}

const AUDIT_CHIP_META: Record<
  string,
  { label: string; color: 'info' | 'success' | 'error' }
> = {
  [GQL.AuditStatusType.UnderAudit]: { label: 'Under audit', color: 'info' },
  [GQL.AuditStatusType.Audited]: { label: 'Audited', color: 'success' },
  [GQL.AuditStatusType.AuditRejected]: { label: 'Rejected', color: 'error' },
};

/** Compact audit-status indicator for the assessments table. */
const AuditStatusChip: React.FC<{
  auditStatus?: GQL.AuditStatusType | null;
}> = ({ auditStatus }) => {
  const meta = auditStatus ? AUDIT_CHIP_META[auditStatus] : undefined;
  if (!meta) {
    return (
      <Text sz="body14" color="text.secondary">
        —
      </Text>
    );
  }
  return (
    <Chip
      label={meta.label}
      size="small"
      color={meta.color}
      variant="outlined"
    />
  );
};

const GOVERNANCE_TAB_SPEC: {
  id: AssessmentGovernanceTab;
  label: string;
  Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>;
}[] = [
  { id: 'screening', label: 'Screening', Icon: ManageSearchOutlinedIcon },
  { id: 'assessments', label: 'Assessments', Icon: AssignmentOutlinedIcon },
  { id: 'testing', label: 'Testing', Icon: ScienceOutlinedIcon },
  { id: 'monitoring', label: 'Monitoring', Icon: MonitorHeartOutlinedIcon },
  {
    id: 'mitigation',
    label: 'Mitigations',
    Icon: NotificationsOutlinedIcon,
  },
];

const GovernanceImpl: React.FC<Props> = ({
  className,
  assessments,
  project_,
  refetch,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { navigateTo: aiNavigateTo } = aiDashboardUtils.useHelper();
  const { navigateTo: functionalNavigateTo } =
    functionalModelsRouteUtil.useHelper();
  const { navigateTo: candidateNavigateTo } =
    candidateModelsDetailsRouteUtil.useHelper();
  const { navigateTo: candidateAgentNavigateTo } =
    candidateAgentsDetailsRouteUtil.useHelper();
  const { navigateTo } = aiSystemAssessmentRouteUtil.useHelper();
  const { projectId, functionalModelId, candidateId, agentId } = useParams();
  // const isAllowedToChangeStatus = isUserProjectLeadOrOwner(
  //   project_,
  //   userProfile.email ?? ''
  // );
  const effectiveId = candidateId ?? agentId ?? functionalModelId ?? projectId;
  const isAllowedToChangeStatus = false;
  const aiSystemHelper = aiSystemAssessmentRouteUtil.useHelper();
  const functionalModelHelper = functionalModelAssessmentRouteUtil.useHelper();
  const candidateModelHelper = candidateModelAssessmentRouteUtil.useHelper();
  const candidateAgentHelper = candidateAgentAssessmentRouteUtil.useHelper();
  const { contextType, loading, refetchProjectFromId } =
    useDashboardDataContext();

  const userProfile = useSelector((state) => state.userProfile);
  const { userProfile: userProfile_ } = useUserProfile();
  const { hasCapability } = usePermissions();
  const Snackbar = useSnackbarContext();
  const [removeAssessment, { loading: removeAssessmentLoading }] =
    GQL.useRemoveAssessmentMutation();
  const [createAssessmentVersion, { loading: isCreatingVersion }] =
    GQL.useCreateAssessmentVersionMutation();
  const [loadingAssessmentId, setLoadingAssessmentId] = React.useState<
    string | null
  >(null);
  const [assignmentManagerOpen, setAssignmentManagerOpen] =
    React.useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = React.useState(false);
  const [selectedAssessmentId, setSelectedAssessmentId] = React.useState<
    string | null
  >(null);
  const [assessmentsSearchInput, setAssessmentsSearchInput] =
    React.useState('');
  const debouncedAssessmentsSearch = useDebounce(assessmentsSearchInput, 500);
  const [statusFilter, setStatusFilter] = React.useState<
    GQL.AssessmentStatusType | ''
  >('');
  const [alignmentFilter, setAlignmentFilter] = React.useState<
    GQL.AssessmentRiskStatusType | ''
  >('');

  const [governanceTab, setGovernanceTab] =
    React.useState<AssessmentGovernanceTab>('screening');
  // Monitoring is Witness, a beta feature.
  const { isBeta } = useIsBetaUser();
  const governanceTabs = React.useMemo(
    () => GOVERNANCE_TAB_SPEC.filter(({ id }) => isBeta || id !== 'monitoring'),
    [isBeta]
  );

  const tabScopedAssessments = React.useMemo(
    () =>
      assessments.filter(
        (a) => getAssessmentGovernanceTab(a) === governanceTab
      ),
    [assessments, governanceTab]
  );

  const filteredAssessments = React.useMemo(() => {
    let result = tabScopedAssessments;
    if (debouncedAssessmentsSearch?.trim()) {
      const term = debouncedAssessmentsSearch.trim().toLowerCase();
      result = result.filter((a) => {
        const nameMatch = a.name?.toLowerCase().includes(term);
        const policyMatch = a.policy?.name?.toLowerCase().includes(term);
        return nameMatch || policyMatch;
      });
    }
    if (statusFilter) {
      result = result.filter((a) => a.status === statusFilter);
    }
    if (alignmentFilter) {
      result = result.filter((a) => a.riskStatus === alignmentFilter);
    }
    return result;
  }, [
    tabScopedAssessments,
    debouncedAssessmentsSearch,
    statusFilter,
    alignmentFilter,
  ]);

  const assessmentsSubHeader = React.useMemo(() => {
    const entity =
      project_?.projectType === GQL.ProjectType.Organization
        ? 'organization'
        : contextType.toLowerCase();
    if (assessments.length === 0) {
      return `No policies are associated with your ${entity}`;
    }
    if (tabScopedAssessments.length === 0) {
      if (governanceTab === 'monitoring') {
        return 'No monitoring controls attached to this project yet.';
      }
      const spec = GOVERNANCE_TAB_SPEC.find((t) => t.id === governanceTab);
      const phrase = spec
        ? `${spec.label.toLowerCase()} assessments`
        : 'assessments in this category';
      return `No ${phrase} for this ${entity}.`;
    }
    if (filteredAssessments.length > 0) {
      return `These assessments are associated with your ${entity}`;
    }
    return 'No assessments match your search or filters in this tab.';
  }, [
    assessments.length,
    tabScopedAssessments.length,
    filteredAssessments.length,
    project_?.projectType,
    contextType,
    governanceTab,
  ]);

  const isAdmin = hasCapability('platform:manage_system_admins');
  // Statuses that block answer generation (assessment is in approval workflow or finalized)
  const restrictedStatuses = [
    GQL.AssessmentStatusType.InReview,
    GQL.AssessmentStatusType.Reviewed,
    GQL.AssessmentStatusType.WaitingForApproval,
    GQL.AssessmentStatusType.Approved,
    GQL.AssessmentStatusType.Published,
  ];

  const ownerEmail = project_?.sharedWith?.find(
    (member) => member?.role === 'Owner'
  )?.email;
  const isProjectOwner = ownerEmail === userProfile_?.email;
  // project:manage_assessments is a project-scoped capability; the context-wide
  // myPermissions query only carries org-level roles, so we also check direct
  // project ownership to avoid hiding the button from the project Owner.
  // org:view_governance is also required because the add-policy page loads the
  // policy list via a governance-gated GraphQL query; without it the user lands
  // on a Forbidden error even if they can otherwise manage assessments.
  const canManageAssessments =
    (isProjectOwner || hasCapability('project:manage_assessments')) &&
    hasCapability('org:view_governance');

  // Project Auditors can bulk-assign audits even if they aren't managers.
  const isProjectAuditor = (project_?.sharedWith ?? []).some(
    (m) =>
      m?.email?.toLowerCase() === userProfile_?.email?.toLowerCase() &&
      (m?.role ?? '').toLowerCase() === 'auditor'
  );

  const handleAddAssessment = () => {
    if (candidateId && functionalModelId && projectId) {
      candidateNavigateTo('ADD_PROJECT_POLICY', {
        params: {
          projectId,
          functionalModelId,
          candidateId,
        },
      });
    } else if (agentId && functionalModelId && projectId) {
      candidateAgentNavigateTo('ADD_PROJECT_POLICY', {
        params: {
          projectId,
          functionalModelId,
          agentId,
        },
      });
    } else if (functionalModelId && projectId) {
      functionalNavigateTo('ADD_PROJECT_POLICY', {
        params: {
          projectId,
          functionalModelId,
        },
      });
    } else if (projectId) {
      aiNavigateTo('ADD_PROJECT_POLICY', {
        params: { projectId },
      });
    }
  };

  // On a use case, open the detail UNDER the use case. The AI-system route makes
  // the page read the parent system's assessments, which never include a use
  // case's, so a use case's monitoring policy opened to a page with no details.
  const goToMonitoringDetail = (assessmentId: string) => {
    if (functionalModelId && projectId && !candidateId && !agentId) {
      functionalNavigateTo('MONITORING_DETAIL', {
        params: { projectId, functionalModelId, assessmentId },
      });
    } else {
      aiNavigateTo('MONITORING_DETAIL', { params: { assessmentId } });
    }
  };

  const goToAssessWelcome = (id: string) => {
    navigateToAssessment({
      assessmentId: id,
      project: { projectId, functionalModelId, candidateId, agentId },
      routeUtils: {
        aiSystem: aiSystemHelper,
        functionalModel: functionalModelHelper,
        candidateModel: candidateModelHelper,
        candidateAgent: candidateAgentHelper,
      },
    });

    // navigateTo('LANDING', {
    //   params: { assessmentId: id },
    //   replace: true,
    // });
  };

  const handleExportAssessments = () => {
    // Filter out archived assessments from the (search-filtered) list
    const toExport = filteredAssessments.filter(
      (assessment) => assessment.status !== 'ARCHIVED'
    );

    // Define headers specific to assessments
    const headers = [
      'Name',
      'Policy Alignment',
      'Assessment Completion',
      'Status',
    ];

    // Generate timestamp for filename
    const today = new Date();
    const timestamp = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const exportType =
      project_?.projectType === GQL.ProjectType.Organization
        ? 'organization'
        : contextType.toLowerCase();
    const filename = `${exportType}_assessments_${timestamp}.csv`;

    // Define mapping function for assessment data
    const mapAssessmentToRow = (assessment: GQL.AssessmentFragment) => [
      assessment.policy?.name || '',
      assessment.riskStatus === GQL.AssessmentRiskStatusType.Low
        ? 'Compliant'
        : assessment.riskStatus === GQL.AssessmentRiskStatusType.Medium
          ? 'Partially Compliant'
          : 'Not Compliant',
      assessment.completionPercent !== undefined
        ? assessment.completionPercent
        : 0,
      assessment.status || '',
    ];

    // Export the CSV
    exportToCsv(filename, headers, toExport, mapAssessmentToRow);
  };

  const removeAssessmentHandler = (id: string) => {
    if (!projectId) {
      Snackbar.open('Project ID not found', { showOk: true });
      return;
    }

    removeAssessment({
      variables: {
        input: {
          assessmentId: id,
          projectId: effectiveId ?? '',
        },
      },
      // Optimistic cache update: subtract the deleted assessment's contribution
      // from the project's pies, totals and card statuses so the dashboard
      // updates instantly. The server's background recompute will overwrite
      // any drift via the subsequent refetchProjectFromId().
      update: (cache, { data }) => {
        if (!data?.removeAssessment?.success) {
          return;
        }
        const projectCacheId = cache.identify({
          __typename: 'Project',
          _id: effectiveId ?? '',
        });
        const removedCacheId = cache.identify({
          __typename: 'Assessment',
          _id: id,
        });
        if (!projectCacheId || !removedCacheId) {
          return;
        }
        const removed = cache.readFragment<{
          _id: string;
          isTechnologyRiskAssessment?: boolean | null;
          numControls?: number | null;
          numAnswers?: number | null;
          numCompliantControls?: number | null;
          numPartiallyCompliantControls?: number | null;
          numNonCompliantControls?: number | null;
          numUnavailableControls?: number | null;
          riskStatus?: string | null;
        }>({
          id: removedCacheId,
          fragment: gql`
            fragment RemovedAssessmentDelta on Assessment {
              _id
              isTechnologyRiskAssessment
              numControls
              numAnswers
              numCompliantControls
              numPartiallyCompliantControls
              numNonCompliantControls
              numUnavailableControls
              riskStatus
            }
          `,
        });
        if (!removed) {
          return;
        }
        const isTech = !!removed.isTechnologyRiskAssessment;
        const dControls = removed.numControls ?? 0;
        const dAnswers = removed.numAnswers ?? 0;
        const dCompl = removed.numCompliantControls ?? 0;
        const dPartial = removed.numPartiallyCompliantControls ?? 0;
        const dNcompl = removed.numNonCompliantControls ?? 0;
        const dUnavail = removed.numUnavailableControls ?? 0;
        const dTotal = dCompl + dPartial + dNcompl + dUnavail;
        const subtract = (a: number, b: number) => Math.max(0, a - b);

        cache.modify({
          id: projectCacheId,
          fields: {
            assessments(existing: readonly any[] | undefined, { readField }) {
              return (existing ?? []).filter(
                (ref) => readField('_id', ref) !== id
              );
            },
            numControls: (curr: number = 0) => subtract(curr, dControls),
            numAnswers: (curr: number = 0) => subtract(curr, dAnswers),
            completionPercent(_curr, { readField }) {
              const c = (readField('numControls') as number) ?? 0;
              const a = (readField('numAnswers') as number) ?? 0;
              return c > 0 ? (a / c) * 100 : 0;
            },
            numTechnologyRiskControls: (curr: number = 0) =>
              isTech ? subtract(curr, dControls) : curr,
            technologyRiskData(curr: any = null) {
              if (!curr || !isTech) {
                return curr;
              }
              const numCompliant = subtract(curr.numCompliant ?? 0, dCompl);
              const numNonCompliant = subtract(
                curr.numNonCompliant ?? 0,
                dNcompl
              );
              const numNotEvaluated = subtract(
                curr.numNotEvaluated ?? 0,
                dUnavail
              );
              let status: string;
              if (numNonCompliant > 0) {
                status = 'HIGH';
              } else if (numCompliant > 0) {
                status = 'LOW';
              } else {
                status = 'UNAVAILABLE';
              }
              return {
                ...curr,
                numCompliant,
                numNonCompliant,
                numNotEvaluated,
                status,
              };
            },
            alignmentRiskData(curr: any = null) {
              if (!curr || isTech) {
                return curr;
              }
              const aligned =
                removed.riskStatus === 'LOW'
                  ? subtract(curr.aligned ?? 0, 1)
                  : (curr.aligned ?? 0);
              const partiallyCompliant =
                removed.riskStatus === 'MEDIUM'
                  ? subtract(curr.partiallyCompliant ?? 0, 1)
                  : (curr.partiallyCompliant ?? 0);
              const notAvailable =
                removed.riskStatus === 'UNAVAILABLE'
                  ? subtract(curr.notAvailable ?? 0, 1)
                  : (curr.notAvailable ?? 0);
              const notAligned =
                removed.riskStatus === 'HIGH'
                  ? subtract(curr.notAligned ?? 0, 1)
                  : (curr.notAligned ?? 0);
              const numCompliantControls = subtract(
                curr.numCompliantControls ?? 0,
                dCompl
              );
              const numPartiallyCompliantControls = subtract(
                curr.numPartiallyCompliantControls ?? 0,
                dPartial
              );
              const numNonCompliantControls = subtract(
                curr.numNonCompliantControls ?? 0,
                dNcompl
              );
              const numUnavailableControls = subtract(
                curr.numUnavailableControls ?? 0,
                dUnavail
              );
              const numTotalControls = subtract(
                curr.numTotalControls ?? 0,
                dTotal
              );
              const total =
                aligned + partiallyCompliant + notAligned + notAvailable;
              let status: string;
              if (total === 0) {
                status = 'UNAVAILABLE';
              } else if (notAvailable === total) {
                status = 'UNAVAILABLE';
              } else if (notAligned > 0 || notAvailable > 0) {
                status = 'HIGH';
              } else if (partiallyCompliant > 0) {
                status = 'MEDIUM';
              } else {
                status = 'LOW';
              }
              return {
                ...curr,
                aligned,
                partiallyCompliant,
                notAligned,
                notAvailable,
                numCompliantControls,
                numPartiallyCompliantControls,
                numNonCompliantControls,
                numUnavailableControls,
                numTotalControls,
                status,
              };
            },
          },
        });

        // Second pass: residual risk depends on the just-updated tech and
        // alignment data. cache.modify applies field updates atomically at
        // the end of a single call, so readField inside one modifier returns
        // the OLD value of a sibling field. Splitting into a second modify
        // call lets readField pick up the new statuses written above.
        cache.modify({
          id: projectCacheId,
          fields: {
            riskStatus(curr: any, { readField }) {
              const techStatus =
                (readField('technologyRiskData') as any)?.status ?? null;
              const alignmentStatus =
                (readField('alignmentRiskData') as any)?.status ?? null;
              const order = ['UNAVAILABLE', 'LOW', 'MEDIUM', 'HIGH'];
              const techRank = order.indexOf(techStatus ?? 'UNAVAILABLE');
              const alignRank = order.indexOf(alignmentStatus ?? 'UNAVAILABLE');
              const max = Math.max(techRank, alignRank);
              if (max <= 0) {
                return techStatus || alignmentStatus || curr || 'UNAVAILABLE';
              }
              return order[max];
            },
          },
        });
      },
    })
      .then(() => {
        Snackbar.open(`Assessment removed successfully.`, {
          showOk: true,
        });
        // Background safety net — reconcile any drift after the server's
        // recompute lands. The optimistic update above already snapped the
        // pies and totals; this just overwrites with authoritative values.
        refetchProjectFromId?.();
      })
      .catch((err) => {
        console.error('Error removing assessment:', err);
        Snackbar.open(
          `Error removing assessment: ${err.message || 'Unknown error'}`,
          {
            showOk: true,
          }
        );
      });
  };

  const handleAssignmentClick = (assessmentId: string) => {
    setSelectedAssessmentId(assessmentId);
    setAssignmentManagerOpen(true);
  };

  const handleAssignmentManagerClose = () => {
    setAssignmentManagerOpen(false);
    setSelectedAssessmentId(null);
    refetchProjectFromId?.();
  };

  return (
    <>
      <Card
        className={className}
        data-feedback-id="project-governance-table"
        title=""
        subHeader=""
        action={
          <Arranger direction="row" alignItems="center" sx={{ gap: '8px' }}>
            {canManageAssessments && (
              <Tooltip title="Add Assessment">
                <Button
                  icon={AddCircleOutlineIcon}
                  color="primary"
                  size="large"
                  onClick={handleAddAssessment}
                >
                  Add Assessment
                </Button>
              </Tooltip>
            )}
            {(canManageAssessments || isProjectAuditor) &&
              assessments.length > 0 && (
                <Tooltip title="Assign all assessments for review, approval, or audit">
                  <Button
                    icon={AssignmentOutlinedIcon}
                    color="primary"
                    size="large"
                    onClick={() => setBulkAssignOpen(true)}
                  >
                    Bulk Assign
                  </Button>
                </Tooltip>
              )}
            {filteredAssessments.length > 0 && (
              <Tooltip title="Export Assessments">
                <Button
                  icon={FileDownloadIcon}
                  color="primary"
                  size="large"
                  onClick={handleExportAssessments}
                >
                  Export
                </Button>
              </Tooltip>
            )}
          </Arranger>
        }
      >
        <>
          <Tabs
            value={governanceTab}
            onChange={(_, v) => setGovernanceTab(v as AssessmentGovernanceTab)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {governanceTabs.map(({ id, label, Icon }) => (
              <Tab
                key={id}
                value={id}
                label={label}
                icon={<Icon fontSize="small" />}
                iconPosition="start"
              />
            ))}
          </Tabs>
          {assessments.length > 0 && governanceTab === 'monitoring' ? (
            <MonitoringTab
              assessments={tabScopedAssessments}
              canManageAssessments={canManageAssessments}
              removeAssessmentHandler={removeAssessmentHandler}
              removeAssessmentLoading={removeAssessmentLoading}
              onRowClick={goToMonitoringDetail}
              className={className}
            />
          ) : assessments.length > 0 ? (
            <>
              <SearchAndFiltersBar
                placeholder="Search assessments..."
                value={assessmentsSearchInput}
                onChange={setAssessmentsSearchInput}
              >
                <FormControl size="small" className="filter-field">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) =>
                      setStatusFilter(
                        e.target.value as GQL.AssessmentStatusType | ''
                      )
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={GQL.AssessmentStatusType.NotStarted}>
                      Not Started
                    </MenuItem>
                    <MenuItem value={GQL.AssessmentStatusType.InProgress}>
                      In Progress
                    </MenuItem>
                    <MenuItem value={GQL.AssessmentStatusType.InReview}>
                      In Review
                    </MenuItem>
                    <MenuItem
                      value={GQL.AssessmentStatusType.WaitingForApproval}
                    >
                      Waiting for Approval
                    </MenuItem>
                    <MenuItem value={GQL.AssessmentStatusType.Approved}>
                      Approved
                    </MenuItem>
                    <MenuItem value={GQL.AssessmentStatusType.Rejected}>
                      Rejected
                    </MenuItem>
                    <MenuItem value={GQL.AssessmentStatusType.Archived}>
                      Archived
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" className="filter-field">
                  <InputLabel>Compliance</InputLabel>
                  <Select
                    value={alignmentFilter}
                    label="Compliance"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (
                        v === GQL.AssessmentRiskStatusType.Low ||
                        v === GQL.AssessmentRiskStatusType.Medium ||
                        v === GQL.AssessmentRiskStatusType.High ||
                        v === ''
                      ) {
                        setAlignmentFilter(v);
                      }
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={GQL.AssessmentRiskStatusType.Low}>
                      Compliant
                    </MenuItem>
                    <MenuItem value={GQL.AssessmentRiskStatusType.Medium}>
                      Partially Compliant
                    </MenuItem>
                    <MenuItem value={GQL.AssessmentRiskStatusType.High}>
                      Not Compliant
                    </MenuItem>
                  </Select>
                </FormControl>
              </SearchAndFiltersBar>
              {filteredAssessments.length > 0 ? (
                isMobile ? (
                  <div className="cards-grid">
                    {filteredAssessments.map((entry) => (
                      <AssessmentCard
                        key={entry._id}
                        entry={entry}
                        onClick={() => goToAssessWelcome(entry._id)}
                        ownerEmail={ownerEmail}
                        showActions={canManageAssessments}
                        isAdmin={isAdmin}
                        onAssign={handleAssignmentClick}
                        onRemove={removeAssessmentHandler}
                        loadingAssessmentId={loadingAssessmentId}
                        removeLoading={removeAssessmentLoading}
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    <Box className="table-scroll-wrapper">
                      <Table className="assessment-table">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell align="left">
                              <Text sz="title">NAME</Text>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Text sz="title">STAKEHOLDER(S)</Text>
                            </StyledTableCell>
                            {governanceTab !== 'screening' && (
                              <StyledTableCell align="center">
                                <Text sz="title">COMPLIANCE</Text>
                              </StyledTableCell>
                            )}
                            <StyledTableCell align="center">
                              <Text sz="title">COMPLETION</Text>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Text sz="title">STATUS</Text>
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Text sz="title">AUDIT</Text>
                            </StyledTableCell>
                            {canManageAssessments && (
                              <StyledTableCell align="center">
                                <Text sz="title">ACTIONS</Text>
                              </StyledTableCell>
                            )}
                            <StyledTableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredAssessments.map((entry, idx) => {
                            const hasAssignments =
                              entry.assignments?.length &&
                              entry.assignments.length > 0;

                            return (
                              <StyledTableRow
                                key={entry._id}
                                onClick={() => goToAssessWelcome(entry._id)}
                              >
                                <StyledTableCell>
                                  {entry.policy ? (
                                    <PolicyItem
                                      policy={entry.policy}
                                      governanceTab={getAssessmentGovernanceTab(
                                        entry
                                      )}
                                    />
                                  ) : (
                                    <Text sz="body14">{entry.name}</Text>
                                  )}
                                </StyledTableCell>
                                <StyledTableCell>
                                  <div className="assignments-container">
                                    {hasAssignments ? (
                                      entry.assignments?.map((a: any) => {
                                        const email = a?.assignedTo || '';
                                        const fullName =
                                          (a as any)?.assignedToName || '';

                                        const tooltipText = fullName
                                          ? `${fullName} (${email})`
                                          : email;

                                        return (
                                          <Tooltip
                                            key={email}
                                            title={tooltipText}
                                            arrow
                                          >
                                            <Avatar
                                              {...stringAvatar(fullName, theme)}
                                            />
                                          </Tooltip>
                                        );
                                      })
                                    ) : (
                                      <Tooltip title={ownerEmail ?? ''} arrow>
                                        <Avatar
                                          sx={{
                                            backgroundColor: (t) =>
                                              t.palette.background.default,
                                            color: (t) =>
                                              t.palette.text.primary,
                                            fontWeight: 'bold',
                                            width: 32,
                                            height: 32,
                                            fontSize: '12px',
                                          }}
                                        >
                                          {ownerEmail
                                            ? ownerEmail
                                                .split('@')[0]
                                                .split('.')
                                                .map((part) =>
                                                  part.charAt(0).toUpperCase()
                                                )
                                                .join('')
                                                .substring(0, 2)
                                            : 'NA'}
                                        </Avatar>
                                      </Tooltip>
                                    )}
                                  </div>
                                </StyledTableCell>

                                {governanceTab !== 'screening' && (
                                  <StyledTableCell>
                                    <StatusBadge
                                      variant="alignment"
                                      value={
                                        entry.riskStatus ===
                                        GQL.AssessmentRiskStatusType.Low
                                          ? 'fully-compliant'
                                          : entry.riskStatus ===
                                              GQL.AssessmentRiskStatusType
                                                .Medium
                                            ? 'partially-compliant'
                                            : entry.riskStatus ===
                                                GQL.AssessmentRiskStatusType
                                                  .Unavailable
                                              ? entry.isTechnologyRiskAssessment
                                                ? 'incomplete'
                                                : 'unavailable'
                                              : 'not-compliant'
                                      }
                                      align="center"
                                    />
                                  </StyledTableCell>
                                )}
                                <StyledTableCell>
                                  <ProgressWithText
                                    value={entry?.completionPercent}
                                  />
                                </StyledTableCell>
                                <StyledTableCell>
                                  <AssessmentStatusText
                                    status={entry?.status}
                                    align="center"
                                    assessmentID={entry._id}
                                    allowed={isAllowedToChangeStatus}
                                  />
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                  <AuditStatusChip
                                    auditStatus={entry?.auditStatus}
                                  />
                                </StyledTableCell>

                                {canManageAssessments && (
                                  <StyledTableCell>
                                    <Arranger
                                      direction="row"
                                      alignItems="center"
                                      justify="center"
                                      sx={{ gap: '8px' }}
                                    >
                                      <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                      >
                                        <Tooltip title="Assign responders">
                                          <IconButton
                                            color="primary"
                                            onClick={(e: React.MouseEvent) => {
                                              e.stopPropagation();
                                              handleAssignmentClick(entry._id);
                                            }}
                                            size="large"
                                            sx={{ color: 'text.primary' }}
                                          >
                                            <UserPlus />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>

                                      <ModalComponent
                                        {...{
                                          className,
                                          archiveProjectHandler:
                                            removeAssessmentHandler,
                                          entry: {
                                            id: entry._id,
                                            projectName:
                                              entry.policy?.name || entry.name,
                                          },
                                          archiveProjectLoading:
                                            removeAssessmentLoading,
                                          confirmText: `Are you sure you want to delete assessment '${entry.policy?.name}'?`,
                                        }}
                                      />
                                    </Arranger>
                                  </StyledTableCell>
                                )}
                                <StyledTableCell>
                                  <TableAction tertiary>
                                    <Button
                                      icon={ArrowRight}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        goToAssessWelcome(entry._id);
                                      }}
                                    />
                                  </TableAction>
                                </StyledTableCell>
                              </StyledTableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                    <div className="scroll-hint">Swipe to see more columns</div>
                  </>
                )
              ) : (
                <Text sz="body14" color="secondary">
                  {tabScopedAssessments.length === 0
                    ? `No assessments in this tab.`
                    : 'No assessments match your search or filters.'}
                </Text>
              )}
            </>
          ) : null}
        </>
      </Card>

      {/* Assessment Assignment Manager Drawer */}
      {selectedAssessmentId && (
        <AssessmentAssignmentManager
          assessmentId={selectedAssessmentId}
          open={assignmentManagerOpen}
          onClose={handleAssignmentManagerClose}
        />
      )}

      {/* Bulk review/approval assignment for the whole project */}
      <BulkAssignDrawer
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        canManageAssessments={canManageAssessments}
      />
    </>
  );
};

export const Governance = muiStyled(GovernanceImpl)(({ theme }) => {
  return css`
    .assessment-table {
      column-gap: ${theme.spacing(spc.xxxl)};
      align-items: center;
    }

    .assignments-container {
      display: flex;
      flex-direction: row;
      max-width: 200px;
      overflow: hidden;
      align-items: center;
      justify-content: center;
      gap: 4px;
    }

    .assignments-container .MuiAvatar-root {
      border: 1px solid ${theme.palette.divider};
    }

    .assignments-container {
      display: flex;
      flex-direction: row;
      max-width: 200px;
      overflow: hidden;
      align-items: center;
      justify-content: center;
      gap: 4px;
    }

    .assignments-container .MuiAvatar-root {
      border: 1px solid ${theme.palette.divider};
    }

    /* Mobile cards grid */
    .cards-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: ${theme.spacing(2)};
      width: 100%;
    }

    ${theme.breakpoints.down('sm')} {
      .cards-grid {
        grid-template-columns: 1fr;
      }
    }

    /* Table scroll wrapper for narrow/tablet so user can scroll to see all columns */
    .table-scroll-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .scroll-hint {
      display: none;
      text-align: center;
      padding: ${theme.spacing(1)} 0;
      font-size: 0.75rem;
      color: ${theme.palette.text.secondary};
      font-style: italic;
    }

    /* Show scroll hint on tablet sizes when table might overflow */
    ${theme.breakpoints.between('sm', 'lg')} {
      .scroll-hint {
        display: block;
      }
    }

    ${theme.breakpoints.up('lg')} {
      .scroll-hint {
        display: none;
      }
    }

    /* Add responsive spacing for small screens */
    @media (max-width: 768px) {
      .assessment-table {
        gap: ${theme.spacing(spc.between_vertical_s)}
          ${theme.spacing(spc.between_horizontal_s)} !important;
        overflow-x: auto;
      }
      .assignments-container {
        max-width: 150px !important;
        gap: 2px !important;
      }
    }
  `;
});
